import { EditOutlined, KeyOutlined, PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  type FormInstance,
  type TableColumnsType,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useUsersCreateUser,
  useUsersListUsers,
  useUsersUpdateUser,
} from '@/api/generated/endpoints/users/users';
import type { UserOut, UserRole } from '@/api/generated/model';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { problemMessage } from '@/i18n/problem';
import { applyProblem } from '@/lib/problemForm';
import { generatePassword } from '@/lib/password';
import { invalidatePath } from '@/lib/queryCache';
import { useIsMobile } from '@/lib/useIsMobile';

const DATETIME = 'DD.MM.YYYY HH:mm';
const ROLES: UserRole[] = ['storekeeper', 'manager', 'admin'];
const LOGIN_PATTERN = /^[a-z0-9._-]{3,64}$/; // как Login на бэкенде (после приведения к строчным)
const MIN_PASSWORD = 10; // security.MIN_PASSWORD_LENGTH

type Editing = { user: UserOut | 'new'; mode: 'edit' } | { user: UserOut; mode: 'password' };

/** Сотрудники (раздел администратора): завести, сменить роль, отключить, выдать новый пароль. */
export function UsersPage() {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const { user: me } = useCurrentUser();
  const [editing, setEditing] = useState<Editing | null>(null);
  const list = useUsersListUsers();

  const roleTag = (r: UserRole) => (
    <Tag color={r === 'admin' ? 'volcano' : r === 'manager' ? 'blue' : 'default'}>
      {t(`roles.${r}`)}
    </Tag>
  );
  const nameCell = (u: UserOut) => (
    <Space size={4} wrap>
      {u.full_name}
      {u.id === me?.id && <Tag color="green">{t('users.you')}</Tag>}
      {!u.is_active && <Tag>{t('users.disabled')}</Tag>}
    </Space>
  );
  const actions: TableColumnsType<UserOut>[number] = {
    key: 'actions',
    width: 90,
    render: (_: unknown, u) => (
      <Space size={0}>
        <Button
          type="text"
          icon={<EditOutlined />}
          aria-label={t('common.edit')}
          onClick={() => {
            setEditing({ user: u, mode: 'edit' });
          }}
        />
        {u.id !== me?.id && (
          <Button
            type="text"
            icon={<KeyOutlined />}
            aria-label={t('users.newPassword')}
            title={t('users.newPassword')}
            onClick={() => {
              setEditing({ user: u, mode: 'password' });
            }}
          />
        )}
      </Space>
    ),
  };

  const columns: TableColumnsType<UserOut> = [
    { key: 'name', title: t('users.colName'), render: (_: unknown, u) => nameCell(u) },
    { key: 'login', title: t('users.colLogin'), dataIndex: 'login', width: 180 },
    {
      key: 'role',
      title: t('users.colRole'),
      dataIndex: 'role',
      width: 160,
      render: (r: UserRole) => roleTag(r),
    },
    {
      key: 'last_login_at',
      title: t('users.colLastLogin'),
      dataIndex: 'last_login_at',
      width: 160,
      render: (v: string | null) => (v ? dayjs(v).format(DATETIME) : t('users.never')),
    },
    actions,
  ];

  // Телефон: сотрудник одной ячейкой — ФИО, роль, логин; кнопки — отдельной колонкой.
  const mobileColumns: TableColumnsType<UserOut> = [
    {
      key: 'name',
      title: t('users.colName'),
      render: (_: unknown, u) => (
        <Space direction="vertical" size={2}>
          {nameCell(u)}
          <Space size={4} wrap>
            {roleTag(u.role)}
            <Typography.Text type="secondary">{u.login}</Typography.Text>
          </Space>
        </Space>
      ),
    },
    actions,
  ];

  return (
    <Card
      title={t('users.title')}
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditing({ user: 'new', mode: 'edit' });
          }}
        >
          {t('users.add')}
        </Button>
      }
    >
      <Typography.Paragraph type="secondary">{t('users.hint')}</Typography.Paragraph>
      <Table<UserOut>
        rowKey="id"
        size="small"
        columns={mobile ? mobileColumns : columns}
        dataSource={list.data}
        loading={list.isFetching}
        pagination={false}
      />
      <UserModal
        user={editing?.mode === 'edit' ? editing.user : null}
        isSelf={editing?.mode === 'edit' && editing.user !== 'new' && editing.user.id === me?.id}
        onClose={() => {
          setEditing(null);
        }}
      />
      <PasswordModal
        user={editing?.mode === 'password' ? editing.user : null}
        onClose={() => {
          setEditing(null);
        }}
      />
    </Card>
  );
}

function PasswordField({ form }: { form: FormInstance }) {
  const { t } = useTranslation();
  return (
    <Form.Item
      label={t('users.password')}
      extra={t('users.passwordHint')}
      htmlFor="password"
      required
    >
      <Space.Compact style={{ width: '100%' }}>
        <Form.Item
          name="password"
          noStyle
          rules={[
            { required: true, message: t('users.passwordRequired') },
            { min: MIN_PASSWORD, message: t('users.passwordShort', { count: MIN_PASSWORD }) },
          ]}
        >
          <Input autoComplete="new-password" maxLength={256} />
        </Form.Item>
        <Button
          onClick={() => {
            form.setFieldValue('password', generatePassword());
            void form.validateFields(['password']);
          }}
        >
          {t('users.generate')}
        </Button>
      </Space.Compact>
    </Form.Item>
  );
}

interface UserForm {
  login: string;
  full_name: string;
  role: UserRole;
  password: string;
  is_active?: boolean;
}

function UserModal({
  user,
  isSelf,
  onClose,
}: {
  user: UserOut | 'new' | null;
  isSelf: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<UserForm>();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const isNew = user === 'new';
  const handlers = {
    onSuccess: async () => {
      await invalidatePath(queryClient, '/api/v1/users');
      onClose();
    },
    onError: (e: unknown) => {
      if (!applyProblem(form, e, { login_exists: 'login' })) setError(problemMessage(e));
    },
  };
  const create = useUsersCreateUser({ mutation: handlers });
  const update = useUsersUpdateUser({ mutation: handlers });

  return (
    <Modal
      title={isNew ? t('users.new') : t('users.edit')}
      open={user !== null}
      onCancel={() => {
        setError(null);
        onClose();
      }}
      okText={t('common.save')}
      okButtonProps={{ loading: create.isPending || update.isPending }}
      onOk={() => {
        form.submit();
      }}
      destroyOnHidden
    >
      <Form<UserForm>
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={
          user && user !== 'new'
            ? {
                login: user.login,
                full_name: user.full_name,
                role: user.role,
                is_active: user.is_active,
              }
            : { role: 'storekeeper', password: generatePassword() }
        }
        onFinish={(v) => {
          setError(null);
          if (isNew) {
            create.mutate({
              data: { login: v.login, full_name: v.full_name, role: v.role, password: v.password },
            });
          } else if (user) {
            update.mutate({
              userId: user.id,
              data: isSelf
                ? { full_name: v.full_name }
                : {
                    full_name: v.full_name,
                    role: v.role,
                    ...(v.is_active === undefined ? {} : { is_active: v.is_active }),
                  },
            });
          }
        }}
      >
        <Form.Item
          name="full_name"
          label={t('users.fullName')}
          rules={[{ required: true, whitespace: true, message: t('users.fullNameRequired') }]}
        >
          <Input maxLength={200} autoFocus />
        </Form.Item>
        <Form.Item
          name="login"
          label={t('users.login')}
          extra={isNew ? t('users.loginHint') : t('users.loginFixed')}
          normalize={(v: string) => v.toLowerCase()}
          rules={[
            { required: true, message: t('users.loginRequired') },
            {
              validator: (_, v: string | undefined) =>
                !v || LOGIN_PATTERN.test(v.trim())
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('users.loginFormat'))),
            },
          ]}
        >
          <Input disabled={!isNew} maxLength={64} autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="role"
          label={t('users.role')}
          extra={isSelf ? t('users.selfHint') : t('users.roleHint')}
        >
          <Select
            disabled={isSelf}
            options={ROLES.map((r) => ({ value: r, label: t(`roles.${r}`) }))}
          />
        </Form.Item>
        {isNew && <PasswordField form={form} />}
        {!isNew && !isSelf && (
          <Form.Item
            name="is_active"
            label={t('users.access')}
            extra={t('users.accessHint')}
            valuePropName="checked"
          >
            <Switch checkedChildren={t('users.allowed')} unCheckedChildren={t('users.denied')} />
          </Form.Item>
        )}
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}

function PasswordModal({ user, onClose }: { user: UserOut | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ password: string }>();
  const [error, setError] = useState<string | null>(null);
  const update = useUsersUpdateUser({
    mutation: {
      onSuccess: onClose,
      onError: (e: unknown) => {
        if (!applyProblem(form, e)) setError(problemMessage(e));
      },
    },
  });

  return (
    <Modal
      title={t('users.passwordTitle', { name: user?.full_name ?? '' })}
      open={user !== null}
      onCancel={() => {
        setError(null);
        onClose();
      }}
      okText={t('common.save')}
      okButtonProps={{ loading: update.isPending }}
      onOk={() => {
        form.submit();
      }}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t('users.passwordResetHint')}</Typography.Paragraph>
      <Form<{ password: string }>
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ password: generatePassword() }}
        onFinish={(v) => {
          setError(null);
          if (user) update.mutate({ userId: user.id, data: { password: v.password } });
        }}
      >
        <PasswordField form={form} />
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}
